import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthUserDTO } from "@platform/shared-types";
import { LoginModal } from "../components/LoginModal";
import { logout } from "../lib/platform-auth";
import { setAuthRequiredHandler } from "../lib/auth-required";
import { useAuthStore } from "../stores/auth-store";

type AuthContextValue = {
  isAuthenticated: boolean;
  user: AuthUserDTO | null;
  requireAuth: (action: () => void) => boolean;
  openLogin: (onSuccess?: () => void) => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const [modalOpen, setModalOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const finishLogin = useCallback(() => {
    setModalOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  }, []);

  const openLogin = useCallback((onSuccess?: () => void) => {
    pendingActionRef.current = onSuccess ?? null;
    setModalOpen(true);
  }, []);

  const requireAuth = useCallback(
    (action: () => void) => {
      if (accessToken) {
        action();
        return true;
      }
      openLogin(action);
      return false;
    },
    [accessToken, openLogin],
  );

  useEffect(() => {
    setAuthRequiredHandler((retry) => {
      openLogin(retry);
    });
    return () => setAuthRequiredHandler(null);
  }, [openLogin]);

  const value: AuthContextValue = {
    isAuthenticated: Boolean(accessToken),
    user,
    requireAuth,
    openLogin,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {modalOpen ? (
        <LoginModal
          onClose={() => {
            setModalOpen(false);
            pendingActionRef.current = null;
          }}
          onSuccess={finishLogin}
        />
      ) : null}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
